import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Field, Textarea, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { mapPersonasToCompanyGroupUser } from '../../../utils/PersonaHelpers';
import IPersonaWithGUID from '../../../utils/types/IPersonaWithGUIDs';
import ICompanyGroupStatUsers from '../../../utils/types/ICompanyGroupsStatUsers';
import CompanyPicker from '../detail/CompanyGroupCompanyPicker';
import ICompanyGroupCompany from '../../../utils/types/ICompanyGroupCompany';
import CompanyGroupUsersPicker from '../detail/CompanyGroupUsersPicker';
import ICompanyGroupFormProps from './CompanyGroupFormProps';
import ITag from '../../../utils/types/ITag';

const CompanyGroupForm: React.FunctionComponent<ICompanyGroupFormProps> = ({
  companyGroup,
  form,
}: ICompanyGroupFormProps): JSX.Element => {
  const companyGroupFormState = { ...companyGroup };
  const [, setCompanyGroupAdmins] = useState([]);
  const [companyGroupCompanies, setCompanyGroupCompanies] = useState(
    companyGroupFormState.companyGroupCompanies.map((companyGroupCompany) => {
      const newObject: ITag = {
        key: companyGroupCompany.companyId.toString(),
        name: `${companyGroupCompany.companyCode} - ${companyGroupCompany.entityLegalName}`,
      };
      return newObject;
    }),
  );
  const { control, errors, setValue, trigger, formState } = form;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (newValue: any, valueKey: string): void => {
    setValue(valueKey, newValue);
    if (formState.isSubmitted) {
      trigger();
    }
  };

  React.useEffect(() => {
    if (companyGroupFormState.companyGroupsStatUsers) {
      const statCompanyGroupUsers: ICompanyGroupStatUsers[] =
        companyGroupFormState.companyGroupsStatUsers.map(
          (user: ICompanyGroupStatUsers): ICompanyGroupStatUsers => {
            return {
              graphId: user.graphId,
            };
          },
        );
      setCompanyGroupAdmins(statCompanyGroupUsers);
    }
  }, [companyGroupFormState.companyGroupsStatUsers]);

  const onAdminPickerUpdate = (inputFieldPersonas: IPersonaWithGUID[]): void => {
    const companyGroupUsers: ICompanyGroupStatUsers[] = mapPersonasToCompanyGroupUser(
      inputFieldPersonas,
      companyGroup.id,
    );
    setCompanyGroupAdmins(companyGroupUsers);
    handleChange(companyGroupUsers, 'companyGroupsStatUsers');
  };

  const onCompanyUpdate = (items: ITag[]): void => {
    const companies = items.map((item) => {
      const intId = typeof item.key === 'string' ? parseInt(item.key, 10) : item.key;
      const newObject: ICompanyGroupCompany = {
        companyId: intId,
        entityLegalName: item.name,
        companyCode: item.name,
      };
      return newObject;
    });
    setCompanyGroupCompanies(items);
    handleChange(companies, 'companyGroupCompanies');
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 25 }}>
        <Controller
          control={control}
          name="name"
          defaultValue={companyGroupFormState.name}
          rules={{
            required: 'Name is required',
            maxLength: {
              value: 150,
              message: 'Input is greater than the maximum character limit of 150.',
            },
            minLength: {
              value: 2,
              message: 'Input is less than the minimum character limit of 2.',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Name" required validationMessage={errors?.name?.message}>
              <Textarea
                id="name"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, textareaData) => onChange(textareaData.value)}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="description"
          defaultValue={companyGroupFormState.description}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Description">
              <Textarea
                id="description"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, textareaData) => onChange(textareaData.value)}
                resize="none"
              />
            </Field>
          )}
        />
        <div>
          <Controller
            name="companyGroupsStatUsers"
            control={control}
            defaultValue={companyGroupFormState.companyGroupsStatUsers}
            render={(): React.ReactElement => (
              <CompanyGroupUsersPicker
                selectedItems={companyGroupFormState.companyGroupsStatUsers}
                onInputFieldUpdate={onAdminPickerUpdate}
                ariaLabelId="companyGroupsStatUsers"
              />
            )}
          />
        </div>
        <Controller
          name="companyGroupCompanies"
          control={control}
          defaultValue={companyGroupFormState.companyGroupCompanies}
          render={(): React.ReactElement => (
            <CompanyPicker
              selectedItems={companyGroupCompanies}
              setSelectedItems={onCompanyUpdate}
              label="Companies"
            />
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default CompanyGroupForm;
